// ══════════════════════════════════════════════════════════════════
//  밤을 낮이 보낸 짐으로 띄운다 — **밤 코드는 한 줄도 안 고친다.**
//
//  ■ `gb.html`/`gb/main.js` 는 **실험실**이다: 무기·HP·배속·렌더를 고르고 버튼을
//    눌러 밤을 켠다. 그건 그대로 둔다(다른 세션의 작업장이고, 계측에 쓰인다).
//    이 파일은 **여정용 입구**다 — 낮이 보낸 짐 하나로 밤을 켜고, 끝나면 낮으로 돌린다.
//
//  ■ ⚠ **왜 씬을 상속하는가.** `gb/scene.js` 의 `create()` 는 World 를 이렇게 만든다:
//        new World(night, { nightKey, weapon, seed: 7, hp,
//                           flockN: FLOCK_START[key], baseLoss: BASE_LOSS[key],
//                           build, rescue })
//    **`seed` 가 리터럴 7 이고 `flockN` 이 config 표에서 온다.** 그리고 낮이 보내는
//    `dayMods`·`dogs` 는 아예 안 넘어간다.
//    그런데 `World` 자신은 그 둘을 **전부 읽는다**(world.js 의 dayMods·dogs).
//    즉 **문은 World 에 나 있고 씬이 그 앞을 막고 있다.**
//    씬을 고치면 다른 세션과 부딪치므로, 여기서 **`this.world` 에 앉는 순간을 가로채**
//    낮 짐을 실은 World 로 갈아 끼운다. 바꾸는 파일은 이 파일 하나다.
//
//  ■ ⚠ **가로채기가 먹었는지 반드시 확인한다.** 씬이 바뀌면 이 수법이 조용히
//    안 먹을 수 있고, 그러면 **낮에서 뭘 하든 밤이 똑같은** 게임이 된다 —
//    가장 나쁜 종류의 고장이다(아무도 안 죽고 아무도 모른다).
//    그래서 켠 직후 값을 대조하고, 안 맞으면 **밤을 시작하지 않고 화면에 적는다.**
//
//  ■ 밤이 끝난 것은 `window.__gbReport` 로 안다. 씬이 밤 끝에 **딱 한 번**
//    부르는 훅이고(scene.js 의 `if (w.over && !this._reported)`), 이미 있던 것이라
//    새로 팔 것이 없다.
// ══════════════════════════════════════════════════════════════════
// 서체 — 낮과 같은 얼굴(daymain.js 머리말). 팝업·존 타이틀·오류 글이 이 서체를 입는다.
import '@fontsource-variable/noto-sans-kr/wght.css';
import Phaser from 'phaser';
import { Graybox } from '../gb/scene.js';
import { World } from '../gb/world.js';
import { NIGHTS, NIGHT_SEC, PIECE } from '../gb/config.js';
import { worldOptsFrom, sceneDataFrom, isPlayable, PLAYABLE_KEYS,
         JOURNEY_AFTER, cardSeedFrom } from './nightopts.js';
import { toNightResult } from '../day/nightio.js';
import { takeToNight, putToNight, putToDay, retargetNightJump,
         ROUTE, go, fadeIn, DAWN } from './handoff.js';
// **화면 채움 규칙은 낮과 공유한다** — 각자 판정하면 갈린다(screenfit.js 머리말).
import { watchFit } from './screenfit.js';
import { StandardGamepadInput } from './gamepad.js';
import { DEVELOPER_BUILD } from './devmode.js';

const note = (html, bad = false) => {
  const el = document.getElementById('note');
  if (el) { el.innerHTML = html; el.className = bad ? 'bad' : ''; el.hidden = false; }
};

// handoff 의 why/key 는 저장소에서 오므로 오류 화면에 넣기 전에 반드시 글자로 만든다.
const escapeHTML = (value) => String(value ?? '').replace(/[&<>"]/g, (ch) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;',
}[ch]));

// ── 낮 짐을 실은 씬 ──────────────────────────────────────────────
class Journey extends Graybox {
  init(d) {
    super.init(d);
    this._pay = d?.dayOpts ?? null;
    this._swapped = false;
    if (!this._pay) return;

    // `this.world = new World(...)` 의 **대입 순간**을 가로챈다. 오른쪽은 이미
    //   만들어진 뒤라 씬이 만든 것 하나가 버려진다 — World 는 순수 계산 객체라
    //   버려도 부작용이 없다(등록·구독을 안 한다). 낭비는 밤당 한 번뿐이다.
    const self = this;
    let w = null;
    Object.defineProperty(this, 'world', {
      configurable: true, enumerable: true,
      get() { return w; },
      set(built) {
        if (self._swapped || !self._pay) { w = built; return; }
        self._swapped = true;
        w = new World(NIGHTS[self.nightKey], worldOptsFrom(self._pay, {
          nightKey: self.nightKey, weapon: self.weapon,
          hp: self.hp, build: self.worldBuild, rescue: self.rescue,
        }));
      },
    });
  }

  // ⚠ **씬이 떴다는 것을 밖에 알리는 유일한 통로.** `game.scene.add()` 는 게임이
  //   부팅되기 전에는 **null 을 돌려준다**(Phaser 가 큐에 넣는다) — 그 반환값을
  //   붙들었다가 `scene.events` 를 부르면 거기서 터지고, 그 아래 `__gbReport` 배선이
  //   통째로 안 걸린다. 실제로 그 일이 났다. 그래서 씬 자신이 알린다.
  //   `super.create()` 를 부르므로 밤의 create 는 한 줄도 안 바뀐다.
  create() {
    super.create();
    onCreated?.(this);
  }
}
let onCreated = null;

// 가로채기가 실제로 먹었는가. **숫자로 확인한다** — 「먹었겠지」는 확인이 아니다.
function auditSwap(scene, p) {
  const w = scene?.world;
  if (!w) return ['World 가 없다 — 씬이 안 떴다'];
  const bad = [];
  if (!scene._swapped) bad.push('가로채기가 한 번도 안 걸렸다 (scene.create 가 바뀌었는가?)');
  if (p.flockN != null && w.flockN !== p.flockN) bad.push(`무리 ${w.flockN} ≠ 낮이 보낸 ${p.flockN}`);
  if (p.dayMods && !w.dayIn) bad.push('낮 계수가 안 실렸다 (world.dayIn 없음)');
  // ⚠ 카드 시드가 씬 기본(고정값)이면 **모든 밤이 같은 3택**을 내고 재시도해도
  //   같은 카드가 나온다 — 기도의 값이 통째로 사라진다. 그것도 대조한다.
  const want = cardSeedFrom(p.seed);
  if (scene.cardSeed !== want) bad.push(`카드 시드 ${scene.cardSeed} ≠ 낮에서 파생한 ${want}`);
  return bad;
}

// ── 부팅 ─────────────────────────────────────────────────────────
const game = new Phaser.Game({
  type: Phaser.WEBGL, parent: 'game', width: 1920, height: 1080,
  scale: { mode: Phaser.Scale.FIT, autoCenter: Phaser.Scale.CENTER_BOTH },
  backgroundColor: '#0b0f1c', render: { antialias: false },
  scene: [],
});
game.scene.add('gb', Journey, false);

// ── 화면 채움 ─────────────────────────────────────────────────────
// 창이 16:9 면 필드가 화면을 100% 먹고 테두리가 없다. 아니면 Phaser 가 만든
// 레터박스에 **낮과 같은 테두리**를 두른다(사장님 지시 2026-08-01).
//   ⚠ 무대 비율은 위 `width/height` 와 **같은 수에서 나와야 한다** — 여기 1920×1080 을
//     바꾸면 이 줄도 같이 바뀐다. 두 곳에 손으로 적지 않는다.
watchFit(document.getElementById('game'),
         { aspect: game.config.width / game.config.height });

// **씬은 언제나 매니저에서 꺼낸다.** 붙들어 두지 않는다(위 create 주석 참조).
const gbScene = () => game.scene.getScene('gb');
// 진단 손잡이는 개발 서버에만 둔다. 출시 번들에서는 월드와 게임 인스턴스를 전역으로
// 노출하지 않는다.
if (DEVELOPER_BUILD) {
  window.__nightWorld = () => gbScene()?.world;
  window.__nightGame = () => game;
  window.__night = { get game() { return game; }, get scene() { return gbScene(); },
                     get world() { return gbScene()?.world ?? null; } };
}

const hand = takeToNight();
let handedBack = false;

function installNightJumpPanel() {
  const panel = document.getElementById('night-jump');
  const grid = document.getElementById('night-jump-grid');
  const close = document.getElementById('night-jump-close');
  const jumpEnd = document.getElementById('night-jump-end');
  if (!panel || !grid || !close || !jumpEnd || !hand.ok) return;

  const keys = Object.keys(NIGHTS).sort((a, b) => Number(a.slice(1)) - Number(b.slice(1)));
  grid.innerHTML = keys.map((key) => {
    const name = String(NIGHTS[key]?.name ?? key).split('—').pop().trim();
    const current = key === hand.opts.nightKey;
    return `<button type="button" data-night-jump="${key}"${current ? ' class="current" aria-current="true"' : ''}>`
      + `<span>${key.slice(1).padStart(2, '0')}</span><b>${escapeHTML(name)}</b></button>`;
  }).join('');

  let wasPaused = false;
  const setOpen = (open) => {
    if (open === !panel.hidden) return;
    panel.hidden = !open;
    const scene = gbScene();
    if (open) {
      wasPaused = !!scene?.scene?.isPaused?.();
      if (scene && !wasPaused) scene.scene.pause();
      close.focus();
    } else {
      if (scene && !wasPaused) scene.scene.resume();
      document.getElementById('game')?.focus?.();
    }
  };

  close.addEventListener('click', () => setOpen(false));
  const jumpToEnd = () => {
    const w = gbScene()?.world;
    if (!w || w.over) return;
    w.t = Math.max(w.t, NIGHT_SEC - 3);
    setOpen(false);
  };
  jumpEnd.addEventListener('click', jumpToEnd);
  grid.addEventListener('click', (event) => {
    const button = event.target.closest('button[data-night-jump]');
    if (!button) return;
    const nightKey = button.dataset.nightJump;
    const jumped = retargetNightJump(hand, nightKey);
    if (!jumped.ok) { note(`<strong>밤 점프 실패</strong><span>${escapeHTML(jumped.why)}</span>`, true); return; }
    const written = putToNight(jumped);
    if (!written.ok) { note(`<strong>밤 점프 실패</strong><span>${escapeHTML(written.why)}</span>`, true); return; }
    location.reload();
  });
  window.addEventListener('keydown', (event) => {
    // DEV CHEAT — 출시 전 제거: I를 누를 때마다 밤 상점 지갑에 치즈 1000을 넣는다.
    if (event.key === 'i' || event.key === 'I') {
      event.preventDefault();
      event.stopImmediatePropagation();
      const w = gbScene()?.world;
      if (w) w.piecesHeld = Math.max(0, Number(w.piecesHeld) || 0) + 1000 * PIECE.perCheese;
      return;
    }
    if (event.key === 'j' || event.key === 'J') {
      event.preventDefault();
      event.stopImmediatePropagation();
      jumpToEnd();
      return;
    }
    if (event.key !== 'h' && event.key !== 'H') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    setOpen(panel.hidden);
  }, true);
}

installNightJumpPanel();
if (!hand.ok) {
  // **아무것도 안 켠다.** 짐이 없는데 기본 밤을 켜면, 낮에서 온 줄 알고 논 뒤에
  //   성적이 낮에 안 붙는다 — 조용히 어긋나는 것보다 안 켜는 편이 낫다.
  note(hand.why === 'none'
    ? `<strong>이어갈 밤이 없습니다.</strong>
       <span>낮의 일기를 덮으면 준비한 짐과 함께 이곳으로 넘어옵니다.</span>
       <a href="day.html">낮으로 돌아가기</a>`
    : `<strong>밤의 짐을 열지 못했습니다.</strong>
       <span>${escapeHTML(hand.why)}</span>
       <a href="day.html">낮으로 돌아가기</a>`, true);
} else if (!isPlayable(hand.opts.nightKey)) {
  // **여기까지 오면 안 된다** — 현재 N1~N40은 전부 있지만, 손상된 저장이나
  // 미래의 범위 밖 열쇠가 들어오면 World 생성 전에 진단하고 낮으로 돌려보낸다.
  //   그래도 두는 이유: 이 주소를 직접 열 수 있고, 낮의 막이가 언젠가 새면
  //   그때 **터지는 대신 이 글이 뜬다.**
  //   ⚠ **대체 성적을 만들지 않는다.** 정산을 지어내면 다음 아침 우유통이
  //     「시달린 밤」을 거짓으로 그린다 — 없는 밤의 성적표가 화면에 뜨는 셈이다.
  const key = hand.opts.nightKey;
  note(`<strong>${escapeHTML(key)} 밤은 아직 열리지 않았습니다.</strong>
    <span>현재 이어지는 밤: ${PLAYABLE_KEYS.map(escapeHTML).join(' · ')}</span>
    <a href="day.html">낮으로 돌아가기</a>`, true);
} else {
  const p = hand.opts;
  fadeIn();                                  // 낮이 암전으로 끝났다 — 그 암전에서 열린다
  // Phaser 텍스트는 만들 때의 서체로 **구워진다** — 서체가 늦으면 대체 글꼴로 박제라
  //   씬을 열기 전에 기다린다. 보험 900ms: 서체가 밤을 막는 일은 절대 없다.
  try {
    await Promise.race([
      Promise.all([document.fonts.load('700 22px "Noto Sans KR Variable"'),
                   document.fonts.load('400 16px "Noto Sans KR Variable"')]),
      new Promise((res) => setTimeout(res, 900)),
    ]);
  } catch { /* 서체는 꾸밈이다 — 실패해도 밤은 연다 */ }
  // 씬의 `create()`가 동기적으로 끝나도 놓치지 않도록 시작 전에 콜백을 건다.
  onCreated = (sc) => {
    // init 이 안 읽는 것들은 **인스턴스에 얹는다** — `gb/main.js` 도 같은 방식이다
    //   (applyHud/applyView 가 `sc.hudMode = …` 로 넣는다).
    Object.assign(sc, JOURNEY_AFTER);
    const bad = auditSwap(sc, p);
    if (bad.length) {
      sc.scene.pause();
      note(`<strong>낮의 짐이 밤에 실리지 않았습니다.</strong>
        <span>${bad.map(escapeHTML).join('<br>')}</span>
        <a href="day.html">낮으로 돌아가기</a>`, true);
    } else {
      // 여정의 존 타이틀은 HTML 한 곳이 맡는다. Canvas qText는 같은 이름을 다시
      // 그리지 않도록 빈 문자열로 두되, 회색박스의 객체/업데이트 계약은 보존한다.
      try {
        claimZoneTitle(sc);
        showNightTitle(p.nightKey);
      } catch (e) { console.warn('[밤 이름]', e); }
    }
  };
  game.scene.start('gb', sceneDataFrom(p));
}

// ── 밤이 끝났다 ──────────────────────────────────────────────────
// 씬이 밤 끝에 딱 한 번 부르는 훅. **리포트는 안 쓴다** — 그건 한글 키 표시용이고,
//   낮이 그걸 파싱하면 밤의 표시 포맷이 낮의 계약이 된다(nightio 헤더의 규율).
//   여기서는 「끝났다」는 신호로만 쓰고, 값은 **살아 있는 World 에서** 뽑는다.
window.__gbReport = () => {
  if (handedBack) return;
  handedBack = true;
  const w = gbScene()?.world;
  const res = toNightResult(w);
  if (!res) {
    note(`<strong>밤의 기록을 읽지 못했습니다.</strong>
      <span>성적을 낮의 장부에 넘기지 못해 여기서 멈췄습니다.</span>
      <a href="day.html">낮으로 돌아가기</a>`, true);
    return;
  }
  const put = putToDay(res);
  if (!put.ok) {
    note(`<strong>밤의 기록을 저장하지 못했습니다.</strong>
      <span>${escapeHTML(put.why)}</span>
      <a href="day.html">낮으로 돌아가기</a>`, true);
    return;
  }
  // ── 결과 장면 ── 정지된 전장 위에서 새벽이 올라오고, 「아침으로」가 낮을 연다.
  //   성적의 정본 창구가 아침 젖틀이라는 것은 그대로다. 이 장면은 전투의 큰 지표
  //   셋(처치·치즈·시간)만 남기며 안정성·우유 정산을 미리 말하지 않는다.
  //
  // ⚠ **보험의 모양이 바뀌었다.** 구 보험(시간이 되면 무조건 넘어간다)은 버튼 관문과
  //   양립할 수 없다 — 대신 **팝업 장착이 실패했을 때만** 구 자동 이동으로 눕는다.
  //   팝업이 떴다면 여정은 버튼(클릭·Enter·Space)이 연다.
  try {
    showNightEnd(w, res);
  } catch (e) {
    console.error('[밤 결과 장면] 장착 실패 — 구 자동 이동으로 넘어간다:', e);
    const LEAVE_AT = 900, SAFETY = 2200;
    setTimeout(() => go(ROUTE.day), LEAVE_AT);
    setTimeout(() => { if (!location.pathname.endsWith('day.html')) location.href = ROUTE.day; },
               LEAVE_AT + SAFETY);
  }
};

// ── 밤 종료 결과 장면 ────────────────────────────────────────────
//   내용 확정: **뱀서 표준 3종**(사장님 선택) — 처치 · 치즈 · 버틴 시간.
//   · 처치 = 플레이어 + 개 (밤의 전과 전체 — HUD 어디에도 없던 수라 여기가 첫 자리다)
//   · 치즈 = 그 밤 조각의 총 환전액(toNightResult 의 ③ 분모와 같은 값. HUD 공인 숫자)
//   · 시간 = min(w.t, 밤 길이). 다운이면 쓰러진 시각이 그대로 남는다
//   · **안정성·우유는 안 띄운다** — 무문자 교리(디자인방향 3장) 유지. 성적은 아침 젖틀 몫.
//   결과 상태는 DOM의 data-result="survive|down|rescue" 로도 공개한다. CSS와 브라우저
//   검증이 한국어 표시 문자열을 파싱하지 않고 정확히 같은 계약을 읽는다.
const RESULT_COPY = Object.freeze({
  survive: {
    kind: '생존 기록',
    title: '밤을 넘겼다',
    summary: '동이 트기 전까지 들판을 지켰다.',
    mark: '새벽',
    parting: '지켜 낸 밤은 낮의 숫자가 된다',
  },
  down: {
    kind: '귀환 기록',
    title: '쓰러졌다. 그래도 아침은 온다.',
    summary: '기록은 여기서 멎지만, 지킨 몫은 낮의 장부에 남는다.',
    mark: '귀환',
    parting: '넘어진 자리도 여정의 일부로 남는다',
  },
  rescue: {
    kind: '구출 기록',
    title: '한 마리를 업고 돌아왔다',
    summary: '마지막 어둠을 건너, 잃었던 한 생명을 데려왔다.',
    mark: '구출',
    parting: '긴 여정의 끝에서 새벽이 열린다',
  },
});

const resultState = (result) => result === '다운' ? 'down'
  : result === '구출' ? 'rescue' : 'survive';

const BOSS_RESULT_NAME = Object.freeze({ bear: '곰', lion: '사자' });
const resultCount = (value) => Math.max(0, Math.floor(Number(value) || 0));
const resultAmount = (value) => Math.max(0, Number(value) || 0);

function bossResultLine(boss) {
  const name = BOSS_RESULT_NAME[boss?.kind];
  if (!name) return '';
  const locks = resultCount(boss.attackLocks);
  const bossHits = resultCount(boss.bossHits);
  const contactHits = resultCount(boss.contactHits);
  const avoided = Math.min(locks, resultCount(boss.avoided));
  const parts = [
    `${name} ${boss.cleared ? '격퇴' : '교전'}`,
    `보스 타격 ${bossHits}회`,
    `접촉 피격 ${contactHits}회`,
    `공격 회피 ${avoided}/${locks}회`,
  ];
  if (boss.kind === 'bear') {
    const sec = resultAmount(boss.critWindowSec);
    const ratio = Math.max(0, Math.min(1, resultAmount(boss.critWindowRatio)));
    parts.push(`급소 자리 ${sec.toFixed(1)}초 (${Math.round(ratio * 100)}%)`);
  }
  parts.push(`누적 압박 ${Math.round(resultAmount(boss.pressureDamage)).toLocaleString('ko-KR')}`);
  return parts.join(' · ');
}

function splitNightName(raw, fallbackKey = '') {
  const text = String(raw ?? '').trim();
  const match = text.match(/^밤\s*(\d+)\s*[—-]\s*(.+)$/);
  if (match) return { chapter: `제 ${match[1]} 장`, name: match[2], full: text };
  const no = String(fallbackKey).match(/\d+/)?.[0];
  return {
    chapter: no ? `제 ${no} 장` : '밤의 기록',
    name: text || '이름 없는 밤',
    full: text || '이름 없는 밤',
  };
}

function showNightEnd(w, res) {
  const el = document.getElementById('nightend');
  if (!el) throw new Error('#nightend 가 없다');
  const m = w.m ?? {};
  const kills = (m.killsPlayer ?? 0) + (m.killsDog ?? 0);
  const cheese = Math.round(w.cheese ?? 0);
  const t = Math.min(w.t ?? 0, NIGHT_SEC);
  const mm = Math.floor(t / 60), ss = String(Math.floor(t % 60)).padStart(2, '0');
  const state = resultState(w.result);
  const copy = RESULT_COPY[state];
  const nightKey = w.nightKey ?? hand?.opts?.nightKey ?? '';
  const night = splitNightName(NIGHTS[nightKey]?.name, nightKey);

  el.dataset.result = state;
  document.body.dataset.nightResult = state;
  document.getElementById('ne-chapter').textContent = night.chapter;
  document.getElementById('ne-night').textContent = night.name;
  document.getElementById('ne-kind').textContent = copy.kind;
  document.getElementById('ne-title').textContent = copy.title;
  document.getElementById('ne-summary').textContent = copy.summary;
  const bossLine = bossResultLine(res?.boss);
  const bossEl = document.getElementById('ne-boss');
  if (bossEl) {
    bossEl.textContent = bossLine;
    bossEl.hidden = !bossLine;
  }
  document.getElementById('ne-mark-label').textContent = copy.mark;
  document.getElementById('ne-parting').textContent = copy.parting;
  // 숫자가 **차오른다** — 상용 결과창의 문법. rAF 가 멎은 판(합성 정지)에서도
  // 마지막 줄의 보험이 확정값을 박으므로 숫자가 어긋난 채 남지 않는다.
  countUp(document.getElementById('ne-kills'), kills);
  countUp(document.getElementById('ne-cheese'), cheese);
  document.getElementById('ne-time').textContent = `${mm}:${ss}`;
  document.getElementById('ne-a11y').textContent =
    `${night.full}. ${copy.title} 처치 ${kills}, 치즈 ${cheese}, 버틴 시간 ${mm}분 ${Number(ss)}초.`
    + (bossLine ? ` ${bossLine}.` : '');
  document.getElementById('game')?.setAttribute('aria-hidden', 'true');
  el.hidden = false;
  const btn = document.getElementById('ne-go');
  let left = false;
  let padFrame = 0;
  const leave = () => {
    if (left) return; left = true;
    if (padFrame) cancelAnimationFrame(padFrame);
    el.dataset.leaving = 'true';
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    const label = btn.querySelector('span');
    if (label) label.textContent = '동이 튼다';
    go(ROUTE.day, 520, DAWN);               // 아침은 어둠이 아니라 **새벽빛**으로 연다
    // 암전 이동이 안 터지는 병력(위 보험 주석)이 있으므로 버튼 쪽에도 한 겹 더 —
    // 눌렀는데 못 떠났으면 시간이 데려간다.
    setTimeout(() => { if (!location.pathname.endsWith('day.html')) location.href = ROUTE.day; }, 2200);
  };
  btn.addEventListener('click', leave, { once: true });
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); leave(); }
  });
  const resultPad = new StandardGamepadInput();
  const pollPad = () => {
    if (left) return;
    if (resultPad.read().pressed.confirm) leave();
    else padFrame = requestAnimationFrame(pollPad);
  };
  padFrame = requestAnimationFrame(pollPad);
  btn.focus({ preventScroll: true });
}

// ── 꾸밈 조각 둘 — 밤을 절대 못 막는다(부르는 쪽이 try 로 감싼다) ──
// 밤의 이름("밤 8 — 곰의 전조")을 번호와 이름으로 가른다. ' — ' 가 없으면 통째로 이름.
function showNightTitle(key) {
  const el = document.getElementById('ntitle');
  const raw = NIGHTS[key]?.name ?? '';
  if (!el || !raw) return;
  const title = splitNightName(raw, key);
  document.getElementById('nt-no').textContent = title.chapter;
  document.getElementById('nt-nm').textContent = title.name;
  el.hidden = false;
  // 애니메이션(3.4s)이 끝나면 걷는다. 합성이 멎어 애니가 영영 안 끝나는 판에서도
  // 시간이 무조건 걷는다 — 페이드 보험과 같은 문법.
  setTimeout(() => { el.hidden = true; }, 3600);
}

function claimZoneTitle(scene) {
  // scene.update()는 qText의 visible/alpha를 계속 갱신한다. 객체를 없애거나 메서드를
  // 덮어쓰면 회색박스 계약을 깨므로 텍스트만 비운다. 업데이트되어도 빈 채다.
  if (scene?.qText?.setText) scene.qText.setText('').setVisible(false);
  scene.zoneTitleOwner = 'html';
  document.body.dataset.zoneTitleOwner = 'html';
}

const RM = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
function countUp(el, to, ms = 850) {
  if (!el) return;
  const n = Math.max(0, Math.round(to));
  if (RM || n === 0) { el.textContent = String(n); return; }
  const t0 = performance.now();
  const step = (now) => {
    const k = Math.min(1, (now - t0) / ms), e = 1 - Math.pow(1 - k, 3);
    el.textContent = String(Math.round(n * e));
    if (k < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
  setTimeout(() => { el.textContent = String(n); }, ms + 120);   // 보험 — rAF 가 멎은 판
}

// ⚠ **밤 도중에 떠나도 짐을 지우지 않는다.** 처음엔 지우게 짜 뒀는데, 그 짐 안에
//   캠페인 저장(`save`)이 들어 있다 — 지우면 **밤 하나가 아니라 여정 전체가 증발한다.**
//   짐을 그대로 두면 이 페이지로 다시 들어왔을 때 **같은 밤이 같은 시드로 처음부터**
//   다시 돈다. 그것이 「밤 중간 저장은 없다」의 올바른 모양이다.
//   (낮 화면도 그 짐을 보고 「밤으로 돌아간다」를 띄운다.)
//   → 그래서 이 파일에는 떠날 때 도는 코드가 **없다.** 없는 것이 맞다.
